import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  Tooltip,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import StarRateIcon from "@mui/icons-material/StarRate";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { axiosConfig } from "../../axios/axiosConfig";

const UserRating = () => {
  const [phoneInput, setPhoneInput] = useState("");
  const [phoneList, setPhoneList] = useState([]);
  const [rating, setRating] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchRating = async () => {
    try {
      const response = await axiosConfig({
        method: "get",
        url: "/tnibroadmin/rating-trigger",
      });
      setRating(response.data.response);
    } catch (error) {
      toast.error("Error fetching rating data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRating();
  }, []);

  const handleAddPhone = () => {
    const inputs = phoneInput
      .split(/[\s,]+/)
      .map((num) => num.trim())
      .filter((num) => num.length > 0);

    const validPhones = [];
    const invalidPhones = [];

    inputs.forEach((num) => {
      if (!/^\d{10}$/.test(num)) {
        invalidPhones.push(num);
      } else if (!phoneList.includes(num) && !validPhones.includes(num)) {
        validPhones.push(num);
      }
    });

    if (invalidPhones.length > 0) {
      toast.warning(`Invalid phones: ${invalidPhones.join(", ")}`);
    }
    if (validPhones.length === 0) return;

    setPhoneList([...phoneList, ...validPhones]);
    setPhoneInput("");
  };

  const handleRemovePhone = (phone) => {
    setPhoneList(phoneList.filter((p) => p !== phone));
  };

  const handleSubmit = async () => {
    if (phoneList.length === 0) {
      toast.error("Please add at least one phone number.");
      return;
    }
    try {
      await axiosConfig({
        method: "post",
        url: "/tnibroadmin/rating-trigger",
        data: { phones: phoneList },
      });
      toast.success("Rating trigger sent successfully!");
      setPhoneList([]);
      fetchRating();
    } catch (error) {
      const errMsg = error?.response?.data?.error || "Something went wrong.";
      toast.error(errMsg);
    }
  };

  return (
    <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>
      {/* Header */}
      <Box
        sx={{
          background: "#04343a",
          borderRadius: "12px",
          px: 3,
          py: 2,
          mb: 3,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          boxShadow: "0 4px 20px rgba(26,32,53,0.3)",
        }}
      >
        <StarRateIcon sx={{ color: "#f9c74f", fontSize: 26 }} />
        <Box>
          <Typography sx={{ color: "#ffffff", fontWeight: 700, fontSize: 18, lineHeight: 1.2 }}>
            User Rating
          </Typography>
          <Typography sx={{ color: "#b39ddb", fontSize: 12 }}>
            Trigger rating prompts for specific users
          </Typography>
        </Box>
      </Box>

      {/* Send Rating Card */}
      <Box
        sx={{
          background: "#ffffff",
          borderRadius: "12px",
          boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
          border: "1px solid #e2e8f0",
          overflow: "hidden",
          mb: 3,
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            borderBottom: "1px solid #e2e8f0",
            background: "#04343a",
          }}
        >
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>
            Trigger Rating
          </Typography>
        </Box>

        <Box sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, maxWidth: 480 }}>
            <TextField
              label="Enter Phone Number(s)"
              value={phoneInput}
              size="small"
              onChange={(e) => setPhoneInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddPhone()}
              placeholder="e.g. 9876543210, 9123456780"
              sx={{ flex: 1 }}
            />
            <Tooltip title="Add">
              <IconButton
                onClick={handleAddPhone}
                sx={{
                  background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)",
                  color: "#fff",
                  borderRadius: "8px",
                  "&:hover": { background: "linear-gradient(135deg, #17a349 0%, #148040 100%)" },
                }}
              >
                <AddCircleOutlineIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Phone Chips */}
          {phoneList.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2, maxWidth: 600 }}>
              {phoneList.map((phone, index) => (
                <Chip
                  key={index}
                  label={phone}
                  onDelete={() => handleRemovePhone(phone)}
                  size="small"
                  sx={{
                    background: "#ede7f6",
                    color: "#4527a0",
                    fontWeight: 600,
                    fontSize: 12,
                    "& .MuiChip-deleteIcon": { color: "#dc2626", fontSize: 16 },
                  }}
                />
              ))}
            </Box>
          )}

          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={phoneList.length === 0}
            sx={{
              mt: 3,
              background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)",
              color: "#fff",
              fontWeight: 600,
              fontSize: 13,
              px: 4,
              py: 1,
              borderRadius: "8px",
              textTransform: "none",
              boxShadow: "0 2px 8px rgba(29,185,84,0.4)",
              "&:hover": { background: "linear-gradient(135deg, #17a349 0%, #148040 100%)" },
              "&.Mui-disabled": { background: "#e2e8f0", color: "#9ca3af", boxShadow: "none" },
            }}
          >
            Send Rating Trigger
          </Button>
        </Box>
      </Box>

      {/* History Table */}
      <Box
        sx={{
          background: "#ffffff",
          borderRadius: "12px",
          boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
          border: "1px solid #e2e8f0",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            borderBottom: "1px solid #e2e8f0",
            background: "#04343a",
          }}
        >
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>
            Rating Trigger History
          </Typography>
        </Box>

        {loading ? (
          <Box textAlign="center" p={4}>
            <CircularProgress size={28} sx={{ color: "#4527a0" }} />
          </Box>
        ) : (
          <TableContainer sx={{ overflowX: "auto", overflowY: "auto", maxHeight: "65vh", border: "1px solid #e2e8f0", borderRadius: "8px" }}>
            <Table
            stickyHeader
              size="small"
              sx={{
                width: "100%",
                "& .MuiTableCell-root": {
                  padding: "7px 12px",
                  fontSize: "12px",
                  borderBottom: "1px solid #f0f4f8",
                  whiteSpace: "nowrap",
                },
                "& .MuiTableBody-root .MuiTableRow-root": {
                  "&:hover": { background: "#f0f7ff" },
                  "&:nth-of-type(even)": { background: "#f8fafc" },
                  "&:nth-of-type(even):hover": { background: "#e8f4ff" },
                },
              }}
            >
              <TableHead>
                <TableRow
                  sx={{
                    "& th": {
                      fontWeight: 700,
                      fontSize: "11px",
                      color: "#ffffff",
                      background: "#04343a",
                      textTransform: "uppercase",
                      letterSpacing: "0.4px",
                      padding: "9px 12px",
                      borderBottom: "none",
                    },
                  }}
                >
                  <TableCell>ID</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Phone No.</TableCell>
                  <TableCell>Sent At</TableCell>
                  <TableCell align="center">Seen</TableCell>
                  <TableCell>Seen At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rating &&
                  rating.map((row) => (
                    <TableRow key={row?.id}>
                      <TableCell sx={{ color: "#6b7280", fontSize: "11px" }}>{row?.id}</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#04343a" }}>{row.user}</TableCell>
                      <TableCell>{row.phoneno}</TableCell>
                      <TableCell>{new Date(row.sent_at).toLocaleString()}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={row.seen ? "Yes" : "No"}
                          size="small"
                          sx={{
                            fontSize: "10px",
                            height: 20,
                            fontWeight: 700,
                            background: row.seen ? "#f0fdf4" : "#fef2f2",
                            color: row.seen ? "#166534" : "#dc2626",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {row.seen_at ? (
                          new Date(row.seen_at).toLocaleString()
                        ) : (
                          <Typography sx={{ color: "#9ca3af", fontSize: 12 }}>—</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
};

export default UserRating;
